import React, { useCallback, useRef, useState } from 'react';
import { Upload, ImagePlus } from 'lucide-react';

interface ImageUploaderProps {
  onUpload(files: File[]): void;
  compact?: boolean;
}

export default function ImageUploader({ onUpload, compact }: ImageUploaderProps) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFiles = useCallback((files: FileList | null) => {
    if (!files) return;
    const imageFiles = Array.from(files).filter(f => f.type.startsWith('image/'));
    if (imageFiles.length > 0) onUpload(imageFiles);
  }, [onUpload]);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  if (compact) {
    return (
      <>
        <button
          onClick={() => inputRef.current?.click()}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          style={{
            backgroundColor: 'var(--sage-primary)',
            color: '#1c1b1a',
          }}
          onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--sage-primary-hover)'}
          onMouseLeave={e => e.currentTarget.style.backgroundColor = 'var(--sage-primary)'}
        >
          <ImagePlus size={16} />
          Add Images
        </button>
        <input ref={inputRef} type="file" accept="image/*" multiple hidden onChange={e => handleFiles(e.target.files)} />
      </>
    );
  }

  return (
    <div
      onDragOver={e => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
      onClick={() => inputRef.current?.click()}
      className="flex flex-col items-center justify-center gap-4 rounded-xl cursor-pointer transition-all p-12 border-2 border-dashed"
      style={{
        borderColor: dragging ? 'var(--sage-primary)' : 'var(--sage-white-10)',
        backgroundColor: dragging ? 'color-mix(in srgb, var(--sage-primary) 5%, transparent)' : 'var(--sage-surface)',
      }}
    >
      <div
        className="w-16 h-16 rounded-2xl flex items-center justify-center"
        style={{
          backgroundColor: 'color-mix(in srgb, var(--sage-primary) 15%, transparent)',
          color: 'var(--sage-primary)',
        }}
      >
        <Upload size={28} />
      </div>
      <div className="text-center">
        <p className="text-sm font-medium" style={{ color: 'var(--sage-text)' }}>
          Drop images here or click to browse
        </p>
        <p className="text-xs mt-1" style={{ color: 'var(--sage-text-muted)' }}>
          PNG, JPG, WebP up to 20MB
        </p>
      </div>
      <input ref={inputRef} type="file" accept="image/*" multiple hidden onChange={e => handleFiles(e.target.files)} />
    </div>
  );
}
